import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Flex,
  Heading,
  Card,
  Box,
  DataList,
  Text,
  Grid,
  BoxProps,
  Badge,
  Tooltip,
  Strong,
  Code,
  Progress,
  Tabs,
} from '@radix-ui/themes';

import {
  useGetBlockQuery,
  GetBlockQuery,
} from '../../graphql/queries/GetBlock.generated';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import HashShortener from '../../components/HashShortener';
import BlockTransactionsTable from '../../components/BlockTransactionsTable';
import {
  QuestionMarkCircledIcon,
  CubeIcon,
  ClockIcon,
  CounterClockwiseClockIcon,
  ArchiveIcon,
  GearIcon,
  LightningBoltIcon,
  TokensIcon,
  LayersIcon,
  ActivityLogIcon,
  DashboardIcon,
  MixIcon,
  Component2Icon,
  CopyIcon,
} from '@radix-ui/react-icons';

// Type aliases for the block data from the GetBlock query
type BlockDetailData = NonNullable<GetBlockQuery['block']>;
type BlockIndexerStatusData = NonNullable<GetBlockQuery['block']>;

const BlockMetadataItem = ({
  label,
  children,
  icon,
  highlight = false,
  ...otherProps
}: {
  label: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  highlight?: boolean;
  otherProps?: DataList.ItemProps;
}) => (
  <DataList.Item align="center" {...otherProps}>
    <DataList.Label>
      <Flex align="center" gap="2">
        {icon && <Box color="gray">{icon}</Box>}
        <Text size="2" color="gray" weight="medium">
          {label}
        </Text>
      </Flex>
    </DataList.Label>
    <DataList.Value>
      <Box
        p={highlight ? '2' : '0'}
        style={{
          borderRadius: highlight ? '6px' : '0',
          backgroundColor: highlight ? 'var(--accent-3)' : 'transparent',
        }}
      >
        {children}
      </Box>
    </DataList.Value>
  </DataList.Item>
);

const BlockMetadataCard = ({
  title,
  children,
  icon,
  gradient = false,
  ...otherProps
}: {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  gradient?: boolean;
  otherProps?: BoxProps;
}) => (
  <Card
    size="2"
    asChild
    style={{
      background: gradient
        ? 'linear-gradient(135deg, var(--accent-2) 0%, var(--accent-3) 100%)'
        : undefined,
      border: gradient ? '1px solid var(--accent-6)' : undefined,
    }}
  >
    <Box height="100%" {...otherProps}>
      <Flex align="center" gap="2" mb="3">
        {icon && (
          <Box
            style={{
              padding: '4px',
              borderRadius: '6px',
              backgroundColor: gradient ? 'var(--accent-4)' : 'var(--accent-3)',
              color: gradient ? 'var(--accent-11)' : 'var(--accent-9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '24px',
              minHeight: '24px',
            }}
          >
            {icon}
          </Box>
        )}
        <Heading as="h3" size="3" trim="both" weight="medium">
          {title}
        </Heading>
      </Flex>
      <DataList.Root size="1">{children}</DataList.Root>
    </Box>
  </Card>
);

interface BlockDetailCardProps {
  block: BlockDetailData;
}

const BlockOverviewCard: React.FC<BlockDetailCardProps & BoxProps> = ({
  block,
  ...otherProps
}) => (
  <BlockMetadataCard
    title="Block Overview"
    icon={<CubeIcon />}
    gradient={true}
    {...otherProps}
  >
    <BlockMetadataItem label="Block Number" icon={<DashboardIcon />}>
      <Code size="3" weight="bold" color="blue">
        #{block.number.toLocaleString()}
      </Code>
    </BlockMetadataItem>

    <BlockMetadataItem label="Block Hash" icon={<Component2Icon />}>
      <HashShortener hash={block.hash} />
    </BlockMetadataItem>

    <BlockMetadataItem label="Timestamp" icon={<ClockIcon />}>
      <TimeAgoTooltip value={block.timestamp} />
    </BlockMetadataItem>

    <BlockMetadataItem label="Transactions" icon={<ActivityLogIcon />}>
      <Flex gap="2" align="center">
        <Strong>{block.transactionCount?.toLocaleString() ?? 'Unknown'}</Strong>
        {block.transactionCount === null && (
          <Tooltip content="This can be unknown if the block has not been indexed yet.">
            <QuestionMarkCircledIcon />
          </Tooltip>
        )}
        {block.transactionCount && (
          <Badge
            color={block.transactionCount > 100 ? 'orange' : 'green'}
            size="1"
          >
            {block.transactionCount > 100 ? 'High Activity' : 'Low Activity'}
          </Badge>
        )}
      </Flex>
    </BlockMetadataItem>

    <BlockMetadataItem label="Fee Recipient" icon={<TokensIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Block Reward" icon={<TokensIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Gas Usage" icon={<MixIcon />}>
      <Flex direction="column" gap="1">
        <Text size="2" color="gray">
          Not Available
        </Text>
        <Progress value={0} size="1" color="gray" />
      </Flex>
    </BlockMetadataItem>

    <BlockMetadataItem label="Size" icon={<ArchiveIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Base Fee" icon={<TokensIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>
  </BlockMetadataCard>
);

interface BlockIndexStatusCardProps {
  block: BlockIndexerStatusData;
}

const BlockIndexStatusCard: React.FC<BlockIndexStatusCardProps & BoxProps> = ({
  block,
  ...otherProps
}) => {
  const syncPercentage = block.transactionCount
    ? (block.transactions.totalCount / block.transactionCount) * 100
    : 0;

  return (
    <BlockMetadataCard
      title="Indexing Status"
      icon={<GearIcon />}
      {...otherProps}
    >
      <BlockMetadataItem
        label="First Seen"
        icon={<CounterClockwiseClockIcon />}
      >
        <TimeAgoTooltip value={block.createdAt} />
      </BlockMetadataItem>

      <BlockMetadataItem label="Sync Progress" icon={<DashboardIcon />}>
        <Text size="2" weight="medium">
          {block.transactions.totalCount.toLocaleString()} /{' '}
          {block.transactionCount?.toLocaleString() ?? 'Unknown'}
        </Text>
      </BlockMetadataItem>
    </BlockMetadataCard>
  );
};

const BlockConsensusInfoCard: React.FC<BlockDetailCardProps & BoxProps> = ({
  block,
  ...otherProps
}) => (
  <BlockMetadataCard
    title="Consensus Layer"
    icon={<LayersIcon />}
    {...otherProps}
  >
    <BlockMetadataItem label="Slot" icon={<DashboardIcon />}>
      <Flex align="center" gap="2">
        <Text size="2" color="gray">
          Not Available
        </Text>
        <Badge color="gray" variant="soft" size="1">
          Coming Soon
        </Badge>
      </Flex>
    </BlockMetadataItem>

    <BlockMetadataItem label="Epoch" icon={<ClockIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Proposer" icon={<GearIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Slot Root" icon={<Component2Icon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Graffiti" icon={<MixIcon />}>
      <Flex align="center" gap="2">
        <Text size="2" color="gray">
          Not Available
        </Text>
        <Badge color="purple" variant="soft" size="1">
          🎨
        </Badge>
      </Flex>
    </BlockMetadataItem>
  </BlockMetadataCard>
);

const BlockMEVInfoCard: React.FC<BlockDetailCardProps & BoxProps> = ({
  block,
  ...otherProps
}) => (
  <BlockMetadataCard
    title="MEV Analytics"
    icon={<LightningBoltIcon />}
    {...otherProps}
  >
    <BlockMetadataItem label="Fee Recipient" icon={<TokensIcon />}>
      <Flex align="center" gap="2">
        <Text size="2" color="gray">
          Not Available
        </Text>
        <Badge color="yellow" variant="soft" size="1">
          ⚡
        </Badge>
      </Flex>
    </BlockMetadataItem>

    <BlockMetadataItem
      label="MEV Reward"
      icon={<TokensIcon />}
      highlight={true}
    >
      <Flex align="center" gap="2">
        <Text size="2" color="gray">
          Not Available
        </Text>
        <Badge color="green" variant="soft" size="1">
          💰
        </Badge>
      </Flex>
    </BlockMetadataItem>

    <BlockMetadataItem label="Payout Transaction" icon={<ActivityLogIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>
  </BlockMetadataCard>
);

const BlockBlobInfoCard: React.FC<BlockDetailCardProps & BoxProps> = ({
  block,
  ...otherProps
}) => (
  <BlockMetadataCard
    title="Blob Data (EIP-4844)"
    icon={<ArchiveIcon />}
    {...otherProps}
  >
    <BlockMetadataItem label="Blob Transactions" icon={<ActivityLogIcon />}>
      <Flex align="center" gap="2">
        <Text size="2" color="gray">
          Not Available
        </Text>
        <Badge color="purple" variant="soft" size="1">
          🗂️
        </Badge>
      </Flex>
    </BlockMetadataItem>

    <BlockMetadataItem label="Total Size" icon={<ArchiveIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Utilization" icon={<DashboardIcon />}>
      <Flex direction="column" gap="1">
        <Text size="2" color="gray">
          Not Available
        </Text>
        <Progress value={0} size="1" color="purple" />
      </Flex>
    </BlockMetadataItem>

    <BlockMetadataItem label="Gas Price" icon={<TokensIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>

    <BlockMetadataItem label="Gas Used" icon={<MixIcon />}>
      <Text size="2" color="gray">
        Not Available
      </Text>
    </BlockMetadataItem>
  </BlockMetadataCard>
);

const BlockDetailView = () => {
  const { number } = useParams();
  const { data, loading, error } = useGetBlockQuery({
    variables: {
      number: Number(number),
    },
  });

  if (loading) return <p>Loading...</p>;
  if (error || !data) return <p>Error :( {error?.message}</p>;

  if (!data.block) {
    return (
      <Flex align="center" justify="center" direction="column" gap="4">
        <Text>Not Found</Text>
      </Flex>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box pb="2" px="1">
        <Flex align="center" justify="between">
          <Heading as="h1" size="5" weight="bold">
            Block #{data.block?.number.toLocaleString()}
          </Heading>

          <Flex align="center" gap="3">
            <Flex align="center" gap="1">
              <ClockIcon width="14" height="14" color="gray" />
              <Text size="2" color="gray">
                <TimeAgoTooltip value={data.block?.timestamp} />
              </Text>
            </Flex>
            {/* Only show Latest badge if block is very recent (within last hour) */}
            {new Date().getTime() - new Date(data.block?.timestamp).getTime() <
              3600000 && (
              <Badge color="blue" size="1" variant="soft">
                Latest
              </Badge>
            )}
          </Flex>
        </Flex>
      </Box>

      {/* Tabbed Content */}
      <Box px="1" pb="1" pt="2">
        <Tabs.Root defaultValue="overview">
          <Tabs.List size="2">
            <Tabs.Trigger value="overview">
              <Flex align="center" gap="2">
                <CubeIcon width="16" height="16" />
                Overview
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="transactions">
              <Flex align="center" gap="2">
                <ActivityLogIcon width="16" height="16" />
                Transactions (
                {data.block?.transactionCount?.toLocaleString() || 0})
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="consensus">
              <Flex align="center" gap="2">
                <LayersIcon width="16" height="16" />
                Consensus
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="mev">
              <Flex align="center" gap="2">
                <LightningBoltIcon width="16" height="16" />
                MEV
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger value="blob">
              <Flex align="center" gap="2">
                <ArchiveIcon width="16" height="16" />
                Blob Data
              </Flex>
            </Tabs.Trigger>
          </Tabs.List>

          <Box mt="2">
            <Tabs.Content value="overview">
              <Grid columns={{ initial: '1', md: '2' }} gap="3">
                <BlockOverviewCard block={data.block} />
                <BlockIndexStatusCard block={data.block} />
              </Grid>
            </Tabs.Content>

            <Tabs.Content value="transactions">
              <Card size="1">
                <BlockTransactionsTable blockNumber={data.block.number} />
              </Card>
            </Tabs.Content>

            <Tabs.Content value="consensus">
              <BlockConsensusInfoCard block={data.block} />
            </Tabs.Content>

            <Tabs.Content value="mev">
              <BlockMEVInfoCard block={data.block} />
            </Tabs.Content>

            <Tabs.Content value="blob">
              <BlockBlobInfoCard block={data.block} />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Box>
    </Box>
  );
};

export default BlockDetailView;
